import React, { useState } from 'react';
import {languages, t} from '../../common/LocalizationProvider';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
    Button,
    TextField,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress, InputLabel, Select, MenuItem, FormControl, // For loading indicator
} from '@material-ui/core';
import {useDispatch, useSelector} from "react-redux";
import {languageActions} from "../../store"; // Import Material-UI components

// ... (keep existing imports and the top part of useStyles)

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
    },
    heading: {
        marginBottom: theme.spacing(3),
        fontSize: 36,
        textAlign: 'center',
        color: '#333',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        maxWidth: 400,
        backgroundColor: '#fff',
        borderRadius: 8,
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        padding: theme.spacing(5),
    },
    label: {
        marginTop: theme.spacing(2.5),
        marginBottom: theme.spacing(1.25),
        fontSize: 16,
        fontWeight: 'bold',
        color: '#333',
        width: '100%',
    },
    input: {
        width: '100%',
    },
    button: {
        width: '100%',
        padding: theme.spacing(1.5),
        fontSize: 18,
        fontWeight: 'bold',
        marginTop: theme.spacing(2.5),
    },
    message: {
        marginTop: theme.spacing(2.5),
        fontSize: 20,
        textAlign: 'center',
    },

    // --- Enhanced Dialog Styles ---
    dialogTitle: {
        margin: 0,
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '& .MuiTypography-h6': { // Targets the Typography component within DialogTitle
            fontWeight: 600,
            fontSize: '1.3rem', // Slightly larger and bolder title
        },
    },
    dialogContent: {
        padding: theme.spacing(2, 3, 2.5), // Consistent padding
        '& .MuiDialogContentText-root:first-of-type': { // For the instructional text
            marginBottom: theme.spacing(2.5), // Space below instruction
            fontSize: '1rem',
            color: theme.palette.text.secondary, // Softer color for instruction
        },
    },
    dialogTextField: {
        marginTop: theme.spacing(0.5), // Reduced top margin as TextField has some internal
        marginBottom: theme.spacing(1.5), // Space before any message or actions
    },
    dialogMessage: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1), // Add some bottom margin too
        minHeight: '24px',
        textAlign: 'center',
        fontWeight: 500,
        fontSize: '0.95rem',
        padding: theme.spacing(0.5, 0),
    },
    dialogActions: {
        margin: 0,
        padding: theme.spacing(1.5, 3),
        borderTop: `1px solid ${theme.palette.divider}`,
        justifyContent: 'flex-end', // Align buttons to the right
        '& > *:not(:first-child)': { // Spacing for items in DialogActions
            marginLeft: theme.spacing(1.5),
        },
    },
    buttonWrapper: { // Wrapper for the primary action button with spinner
        position: 'relative', // Needed for absolute positioning of the spinner
        display: 'inline-flex', // Keeps it inline with other buttons if any
    },
    buttonProgress: {
        // color: theme.palette.primary.main, // Inherits color by default
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12, // Half of spinner size (24px / 2)
        marginLeft: -12, // Half of spinner size (24px / 2)
    },
    // --- End of Enhanced Dialog Styles ---

    languageSelectorWrapper: {
        position: 'absolute',
        top: 16,
        right: 16,
        zIndex: 1000, // Make sure it's above other elements
        minWidth: 120,
    },
}));

// ... (keep the rest of your component logic: ConfirmPassword function, state, handlers etc.)
const ChangePassword = () => {
    const classes = useStyles();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const history = useHistory();

    // State for the resend token dialog
    const [isResendDialogOpen, setIsResendDialogOpen] = useState(false);
    const [resendEmail, setResendEmail] = useState('');
    const [resendDialogMessage, setResendDialogMessage] = useState('');
    const [isResending, setIsResending] = useState(false);

    const languageSelected = useSelector(state => state.language.language ? state.language.language : 'en');
    const dispatch = useDispatch();

    const settinglanguage = (e) => {
        dispatch(languageActions.updateLanguage(e.target.value))
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage(''); // Clear previous messages

        if (password === "" || confirmPassword === '') {
            setMessage(t('FillTwoFields'));
            return;
        }
        if (password !== confirmPassword) {
            setMessage(t('Passwords_not_identical'));
            return;
        }

        const urlParts = window.location.hash.split('/');
        const token = urlParts[2];

        if (!token) {
            setMessage(t('Invalidtoken.')); // Or some other appropriate message
            return;
        }

        try {
            const response = await fetch('/api/session/changepassword', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(`password=${password}&token=${token}`),
            });

            if (response.status === 200) { // More robust check than response.status === 200
                history.push('/login');
            } else {
                const errorText = await response.text();
                const errorCode = errorText.split(' ')[0];
                setMessage(t(errorCode || 'UnknownError')); // Set main form message

                // if (errorCode === 'Tokenexpired,pleasesigninagain') {
                    setResendDialogMessage(''); // Clear previous dialog message
                    setIsResendDialogOpen(true); // Open the dialog
                // }
            }
        } catch (error) {
            console.error("Error changing password:", error);
            setMessage(t('NetworkError'));
        }
    };

    const handleResendToken = async () => {
        if (!resendEmail) {
            setResendDialogMessage(t('EmailRequired'));
            return;
        }
        setIsResending(true);
        setResendDialogMessage('');

        try {
            const response = await fetch('/api/session/senemail', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(`email=${resendEmail}`)
            });

            if (response.ok) {
                setResendDialogMessage(t('TokenResentSuccess'));
                // Optionally close the dialog after a delay or let the user close it
                setTimeout(() => setIsResendDialogOpen(false), 10000);
            } else {
                const errorText = await response.text();
                // You might want more specific error handling from your backend
                setResendDialogMessage(t(errorText.split(' ')[0] || 'errorsndrquest'));
            }
        } catch (error) {
            console.error("Error resending token:", error);
            setResendDialogMessage(t('NetworkError'));
        } finally {
            setIsResending(false);
        }
    };

    const handleCloseResendDialog = () => {
        setIsResendDialogOpen(false);
        setResendEmail(''); // Reset email field on close
        setResendDialogMessage(''); // Clear dialog message
    };

    const messageNote = () => { // Simplified, main message state handles most cases
        if (message) return message;
        if (password === "" || confirmPassword === "") return '';
        if (password !== confirmPassword) return t('Passwords_not_identical');
        return t('Passwords_identical');
    };

    return (
        <div className={classes.container}>
            <h1 className={classes.heading}>{t('PasswordReset')}</h1>
            <form onSubmit={handleSubmit} className={classes.form}
                  dir={languageSelected === 'ar' || languageSelected === 'fa' ? 'rtl' : 'ltr'}>
                <label className={classes.label}>
                    {t('NewPassword')}
                    <TextField
                        type="password"
                        variant="outlined"
                        fullWidth
                        value={password}
                        onChange={(event) => {
                            setMessage('');
                            setPassword(event.target.value);
                        }}
                        placeholder={t('NewPassword')}
                        className={classes.input} // You might not need this if using variant="outlined"
                        margin="dense"
                    />
                </label>
                <label className={classes.label}>
                    {t('ConfirmNewPassword')}
                    <TextField
                        type="password"
                        variant="outlined"
                        fullWidth
                        value={confirmPassword}
                        onChange={(event) => {
                            setMessage('');
                            setConfirmPassword(event.target.value);
                        }}
                        placeholder={t('ConfirmNewPassword')}
                        className={classes.input}
                        margin="dense"
                    />
                </label>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className={classes.button}
                >
                    {t('Confirm')}
                </Button>
            </form>

            {messageNote() && (
                <span
                    className={classes.message}
                    style={{
                        color: messageNote() === t('Passwords_identical') ? 'green' : 'red',
                    }}
                >
                    {messageNote()}
                </span>
            )}
            <Dialog
                open={isResendDialogOpen}
                onClose={handleCloseResendDialog}
                aria-labelledby="resend-token-dialog-title"
                maxWidth="xs" // Optional: constrain dialog width
                fullWidth      // Optional: makes dialog use maxWidth
                dir={languageSelected === 'ar' || languageSelected === 'fa' ? 'rtl' : 'ltr'}>
            >
                <DialogTitle id="resend-token-dialog-title" className={classes.dialogTitle}>
                    {t('resendEmail')}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <DialogContentText> {/* Instructional text */}
                        {t('TokenExpiredMessage')}
                    </DialogContentText>
                    <TextField
                        autoFocus
                        // margin="dense" // Removed, using className for margin control
                        id="email-resend"
                        label={t('emailAddress')}
                        type="email"
                        fullWidth
                        variant="outlined"
                        value={resendEmail}
                        autoComplete="email"
                        onChange={(e) => {
                            setResendEmail(e.target.value);
                            if (resendDialogMessage) setResendDialogMessage(''); // Clear message on input change
                        }}
                        className={classes.dialogTextField}
                        disabled={isResending}
                        placeholder={"e.g., user@example.com"}
                        error={!!resendDialogMessage && resendDialogMessage !== t('TokenResentSuccess')} // Show error state on TextField
                        // helperText={resendDialogMessage && resendDialogMessage !== t('TokenResentSuccess') ? resendDialogMessage : ""} // Alternative: show message as helperText
                    />
                    {resendDialogMessage && (
                        <DialogContentText
                            className={classes.dialogMessage}
                            style={{
                                color: resendDialogMessage === t('TokenResentSuccess')
                                    ? "green" // Use theme colors for consistency
                                    : "red",
                            }}
                        >
                            {resendDialogMessage}
                        </DialogContentText>
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseResendDialog}
                        color="inherit" // More subtle cancel button
                        disabled={isResending}
                    >
                        {t('sharedCancel')}
                    </Button>
                    <div className={classes.buttonWrapper}> {/* Wrapper for primary button */}
                        <Button
                            onClick={handleResendToken}
                            color="primary"
                            variant="contained" // Make primary action more prominent
                            disabled={isResending || !resendEmail} // Disable if email is empty or resending
                        >
                            {t('resend')}
                        </Button>
                        {isResending && <CircularProgress size={24} className={classes.buttonProgress} />}
                    </div>
                </DialogActions>
            </Dialog>
            <div className={classes.languageSelectorWrapper}>
                <FormControl variant="filled" fullWidth>
                    <InputLabel>{t('loginLanguage')}</InputLabel>
                    <Select value={languageSelected} onChange={settinglanguage}>
                        {languages.map((it) => (
                            <MenuItem key={it.code} value={it.code}>{it.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>



        </div>
    );
};

export default ChangePassword;